import React, { useState, useSyncExternalStore } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Badge,
  ButtonBase,
  Divider,
  LinearProgress,
  Paper,
  Button,
  Menu,
  MenuItem,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Face as FaceIcon,
  PhotoCamera as PhotoCameraIcon,
  ZoomIn as ZoomInIcon,
  AutoFixHigh as MagicIcon,
  BrushOutlined as BrushIcon,
  ImageSearch as ImageSearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  VideoLibrary as VideoLibraryIcon,
  ContentCut as ScissorsIcon,
  AccountCircle,
  CreditCard,
  Settings,
  History,
  Logout,
  Payment,
  Wallet,
  Menu as MenuIcon,
  SmartToy as RobotIcon,
  Email as EmailIcon,
  Insights as InsightsIcon,
  KeyboardArrowRight,
  PhotoLibrary
} from '@mui/icons-material';
import { Link, navigate } from 'gatsby';
import { useTheme } from '@mui/material/styles';
import CreditsIcon from './CreditsIcon';
import { syncUserBalance } from '../utils/credits';
import { COLORS, GRADIENTS, SHADOWS, TOOL_ACCENTS } from '../theme/tokens';
import { getToolPath, getToolBySlug } from '../config/tools';
import * as jobs from '../utils/jobs';

const TEST_USER_ID = 'dae36dea-dad0-41be-adbe-fed96f561e3a';

// Иконки инструментов — тонкие линейные (feather-подобные) из дизайн-референса
// (design_handoff_airun_redesign/Sidebar.dc.html). color задаёт активный/неактивный цвет.
const ToolGlyph = ({ name, color, size = 18 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };
  switch (name) {
    case 'bot':
      return (
        <svg {...common}>
          <rect x="5" y="8" width="14" height="11" rx="3" />
          <path d="M12 8 V4" />
          <circle cx="12" cy="3.4" r="1.1" fill={color} stroke="none" />
          <circle cx="9.6" cy="13" r="1" fill={color} stroke="none" />
          <circle cx="14.4" cy="13" r="1" fill={color} stroke="none" />
        </svg>
      );
    case 'wand':
      return (
        <svg {...common}>
          <path d="M4 20 L14 10" />
          <path d="M16 4 l1 2 l2 1 l-2 1 l-1 2 l-1 -2 l-2 -1 l2 -1 Z" fill={color} stroke="none" />
        </svg>
      );
    case 'camera':
      return (
        <svg {...common}>
          <rect x="3" y="8" width="18" height="11" rx="2.5" />
          <path d="M8.5 8 L10 5.5 H14 L15.5 8" />
          <circle cx="12" cy="13.5" r="3" />
        </svg>
      );
    case 'spark':
      return (
        <svg {...common}>
          <path d="M11 5 l1.4 4.6 L17 11 l-4.6 1.4 L11 17 l-1.4-4.6 L5 11 l4.6-1.4 Z" fill={color} stroke="none" />
          <path d="M18 15 l.7 2 l2 .7 l-2 .7 l-.7 2 l-.7-2 l-2-.7 l2-.7 Z" fill={color} stroke="none" />
        </svg>
      );
    case 'brush':
      return (
        <svg {...common}>
          <path d="M14 7 L19 4 M13 8 L16 11 L8 19 C6 20 4 18 5 16 Z" />
        </svg>
      );
    case 'retouch':
      // кисть над пунктирным выделением
      return (
        <svg {...common}>
          <rect x="3.5" y="3.5" width="11" height="11" rx="1.5" strokeDasharray="2.4 2.4" />
          <path d="M20.5 10.5 L15 16 M14 15 L17 18 L13.5 21 C12 21.8 10.5 20.5 11.2 19 Z" />
        </svg>
      );
    case 'video':
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="12" rx="3" />
          <path d="M11 10 L11 14 L15 12 Z" fill={color} stroke="none" />
        </svg>
      );
    case 'cut':
      return (
        <svg {...common}>
          <circle cx="6" cy="16.5" r="2.2" />
          <circle cx="6" cy="7.5" r="2.2" />
          <path d="M8 15.5 L19 6.5 M8 8.5 L19 17.5" />
        </svg>
      );
    case 'vector':
      return (
        <svg {...common}>
          <path d="M5 17 C5 9, 19 9, 19 17" />
          <rect x="3.4" y="15.4" width="3.2" height="3.2" rx="0.6" />
          <rect x="17.4" y="15.4" width="3.2" height="3.2" rx="0.6" />
          <path d="M12 4.5 l1 2 l2 1 l-2 1 l-1 2 l-1 -2 l-2 -1 l2 -1 Z" fill={color} stroke="none" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M4 19 V5 M4 19 H20" />
          <path d="M7 15 l3 -4 l3 2 l4 -6" />
        </svg>
      );
    default:
      return null;
  }
};

const SIDEBAR_FULL_WIDTH = 248;
const SIDEBAR_COLLAPSED_WIDTH = 64;

// --- глобальный индикатор очереди (экраны 1o / 1p дизайн-хендофа) -----------------------------

// Снимок для SSR и для гидратации. НЕ jobs.getJobs: во время гидратации React зовёт именно
// третий аргумент useSyncExternalStore, а на клиенте getJobs уже поднимет стор из localStorage
// и вернёт восстановленные задачи — разметка разъедется с серверной, React выкинет всё дерево
// и перерисует корень (тот самый «мигание сайдбара и серый экран», см. navigation-menu-howto.md
// §2). Пустой снимок совпадает с SSR, а реальную очередь React подтянет сразу после гидратации.
const NO_JOBS = [];
const getServerJobs = () => NO_JOBS;

const isQueueJob = (job) => job.status === 'queued' || job.status === 'generating';

const QUEUE_STATUS_TEXT = { queued: 'в очереди', generating: 'генерируется' };

// Кросс-девайса нет (очередь лежит в localStorage этого браузера), поэтому формулировка уже,
// чем в README дизайна: обещаем возврат, а не «считается на сервере, закрывайте что угодно».
const QUEUE_FOOTER_TEXT =
  'Задача продолжается на сервере — страницу можно закрыть и вернуться позже';

// 1 задача / 2 задачи / 5 задач — обычное русское правило, включая 11–14.
const pluralJobs = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'задача';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'задачи';
  return 'задач';
};

// Восстановленная задача может быть старой (стор поднимает задачи возрастом до суток), поэтому
// часы тоже показываем — иначе «125:30» вместо «2:05:30».
const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor(total / 60) % 60;
  const seconds = total % 60;
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}` : `${minutes}:${ss}`;
};

// Кольцо из дизайна (Sidebar.dc.html): дорожка #CFE4FA, головка #2196F3.
const QueueSpinner = ({ size = 16 }) => (
  <Box
    sx={{
      width: `${size}px`,
      height: `${size}px`,
      flexShrink: 0,
      boxSizing: 'border-box',
      borderRadius: '50%',
      border: '2px solid',
      borderColor: COLORS.blueBorder,
      borderTopColor: COLORS.blue,
      animation: 'airunQueueSpin 1s linear infinite',
      '@keyframes airunQueueSpin': { to: { transform: 'rotate(360deg)' } }
    }}
  />
);

// Состояние авторизации читаем синхронно при инициализации, чтобы сайдбар не
// «прыгал» развёрнут→свёрнут→развёрнут при загрузке (см. navigation-menu-howto.md §2).
const readIsLoggedIn = () => {
  if (typeof window === 'undefined') return false;
  try {
    return !!localStorage.getItem('authToken') && !!localStorage.getItem('userData');
  } catch (_) {
    return false;
  }
};

const readUserData = () => {
  if (typeof window === 'undefined') return null;
  try {
    const u = localStorage.getItem('userData');
    return u ? JSON.parse(u) : null;
  } catch (_) {
    return null;
  }
};

// Запоминаем выбор пользователя «свернуть/развернуть» между сессиями.
// null — выбор ещё не сделан (используем дефолт: на десктопе развёрнут).
const SIDEBAR_PREF_KEY = 'sidebarCollapsed';
const readCollapsedPref = () => {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(SIDEBAR_PREF_KEY);
    return v === null ? null : v === 'true';
  } catch (_) {
    return null;
  }
};

const SidebarMenu = ({ selectedTool, onToolSelect, location }) => {
  // ВАЖНО (гидратация): начальные значения должны совпадать с SSR (на сервере нет
  // localStorage → гость, развёрнут). Реальные значения из localStorage выставляем
  // в useEffect ниже. Иначе первый клиентский рендер (залогинен/свёрнут) не совпадёт
  // с серверным, React выкинет всё дерево и перерисует корень — это давало «мигание
  // сайдбара и серый экран» при входе (см. navigation-menu-howto.md §2).
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Глобальная очередь генераций. Источник — стор задач, он же уведомляет об изменениях:
  // счётчику никакой таймер не нужен. getJobs() отдаёт стабильную ссылку, поэтому useMemo
  // пересчитывает фильтр только на реальную запись в стор.
  const allJobs = useSyncExternalStore(jobs.subscribe, jobs.getJobs, getServerJobs);
  const queue = React.useMemo(() => allJobs.filter(isQueueJob), [allJobs]);
  const [queueAnchor, setQueueAnchor] = useState(null);
  const isQueueOpen = Boolean(queueAnchor);

  // Тикаем секунды только пока поповер открыт — таймер живёт ровно столько, сколько его видно.
  const [, setQueueTick] = useState(0);
  React.useEffect(() => {
    if (!isQueueOpen) return undefined;
    const timer = setInterval(() => setQueueTick((v) => v + 1), 1000);
    return () => clearInterval(timer);
  }, [isQueueOpen]);

  // Последняя задача завершилась — плашка-якорь исчезает из DOM, поповер надо закрыть самому,
  // иначе он останется висеть у оторванного anchorEl.
  React.useEffect(() => {
    if (queue.length === 0) setQueueAnchor(null);
  }, [queue.length]);

  // После монтирования применяем реальное состояние сворачивания:
  // мобильный — всегда свёрнут; десктоп — запомненный выбор пользователя
  // (дефолт — развёрнут, гостя НЕ сворачиваем автоматически).
  React.useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    } else {
      const pref = readCollapsedPref();
      setIsCollapsed(pref === null ? false : pref);
    }
  }, [isMobile]);

  // Переключение с сохранением выбора пользователя между сессиями.
  const toggleCollapsed = () => {
    setIsCollapsed((v) => {
      const next = !v;
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(SIDEBAR_PREF_KEY, String(next));
        }
      } catch (_) {}
      return next;
    });
  };

  // Экспортируем ширину сайдбара в CSS-переменную, чтобы контент поджимался.
  // Ширина меняется МГНОВЕННО (без transition на margin-left): анимация layout-свойства
  // заставляла браузер перекладывать всю страницу каждый кадр 200мс — на тяжёлых
  // страницах (сетки картинок) это давало рывки. Плавность даёт fade-in ярлыков
  // (.sidebar-fade-in в global.css). Переменную НЕ удаляем в cleanup — иначе layout
  // прыгает к дефолту и появляется мигание при каждом сворачивании.
  React.useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const width = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_FULL_WIDTH;
    document.documentElement.style.setProperty('--sidebar-width', `${width}px`);
  }, [isCollapsed]);

  // Активный инструмент выводим напрямую из location (его прокидывает
  // wrapPageElement и он меняется на каждой client-side навигации, в т.ч. при
  // navigate() через pushState). Подсветка из URL — см. navigation-menu-howto.md §4.
  const currentTool = React.useMemo(() => {
    const pathname = location?.pathname
      || (typeof window !== 'undefined' ? window.location.pathname : '');
    const search = location?.search
      || (typeof window !== 'undefined' ? window.location.search : '');
    // Отдельные страницы инструментов: /ai-photo/<slug>/ → id из реестра.
    const slugMatch = pathname.match(/^\/ai-photo\/([^/]+)\/?$/);
    if (slugMatch) {
      const bySlug = getToolBySlug(slugMatch[1]);
      if (bySlug) return bySlug.id;
    }
    // Легаси-ссылки /ai-photo/?tool=<id> (в т.ч. скрытая админка).
    if (pathname.includes('/ai-photo') && search) {
      const toolParam = new URLSearchParams(search).get('tool');
      if (toolParam) return toolParam;
    }
    return selectedTool || null;
  }, [location, selectedTool]);

  // Функция для обновления данных пользователя.
  // Сырую строку userData запоминаем в ref: syncUserBalance() дёргается на каждый
  // фокус вкладки и шлёт userDataUpdated, даже если баланс не изменился — без
  // этой проверки весь сайдбар перерисовывался бы на каждое переключение вкладок.
  const lastUserDataRawRef = React.useRef(null);
  const updateUserData = () => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('userData');

    if (token && user) {
      if (user === lastUserDataRawRef.current) return; // данные не изменились
      try {
        const parsed = JSON.parse(user);
        lastUserDataRawRef.current = user;
        setUserData(parsed);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Ошибка парсинга userData:', error);
        lastUserDataRawRef.current = null;
        handleLogout();
      }
    } else {
      lastUserDataRawRef.current = null;
      setIsLoggedIn(false);
    }
  };

  React.useEffect(() => {
    updateUserData();
    
    const handleStorageChange = (e) => {
      if (e.key === 'userData' || e.key === 'authToken') {
        updateUserData();
      }
    };
    
    const handleUserDataUpdate = () => {
      updateUserData();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, []);

  // Синхронизация баланса при возврате на вкладку. Одного 'focus' недостаточно:
  // мобильные браузеры при возврате из банковского приложения (оплата по СБП)
  // и при восстановлении страницы из bfcache шлют visibilitychange/pageshow,
  // а focus может вообще не сработать — баланс оставался старым после оплаты.
  const lastBalanceSyncRef = React.useRef(0);
  React.useEffect(() => {
    const syncNow = () => {
      if (!isLoggedIn) return;
      // события приходят залпом (pageshow+visibilitychange+focus) — не дублируем запрос
      const now = Date.now();
      if (now - lastBalanceSyncRef.current < 5000) return;
      lastBalanceSyncRef.current = now;
      syncUserBalance();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncNow();
    };
    const handlePageShow = (e) => {
      if (e.persisted) syncNow(); // страница восстановлена из bfcache без перезагрузки
    };

    window.addEventListener('focus', syncNow);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('focus', syncNow);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isLoggedIn]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('ai-generation-history');
    localStorage.removeItem('ai-photosession-history');
    localStorage.removeItem('ai-model-history');
    localStorage.removeItem('ai-changingroom-history');
    localStorage.removeItem('ai-object-history');
    localStorage.removeItem('ai-video-history');
    localStorage.removeItem('ai-video-merge-history');
    setUserData(null);
    setIsLoggedIn(false);
    setAnchorEl(null);
    navigate('/login/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Проверка доступа к инструменту auto-product-card (открыт для всех)
  const hasAccessToAutoProductCard = () => {
    return true; // Доступ открыт для всех пользователей
  };

  // Доступ к скрытой админ-панели (только владелец).
  // Гейтим по тому же id, что раньше открывал старые инструменты, + по email как запаску.
  const isAdmin = () => {
    return userData?.id === TEST_USER_ID || userData?.email === 'austinellla@yandex.ru';
  };

  // Группировка инструментов
  const photoTools = [
    { id: 'auto-product-card', name: 'Бот фотосессий', iconKey: 'bot', color: '#10B981', badge: '' },
    { id: 'replace-model', name: 'Фоторедактор', iconKey: 'wand', color: '#6366F1', badge: 'хит' },
    { id: 'retouch', name: 'Ретушь', iconKey: 'retouch', color: '#EC4899', badge: 'новое' },
    { id: 'replace-background', name: 'Ты фотограф', iconKey: 'camera', color: '#8B5CF6', badge: '' },
    { id: 'photosession', name: 'Фотосессия', iconKey: 'spark', color: '#EC4899', badge: '' },
    { id: 'photo-generation', name: 'Генерация фото', iconKey: 'brush', color: '#0EA5E9', badge: '' },
    { id: 'vector-ai', name: 'Векторная AI', iconKey: 'vector', color: '#8B5CF6', badge: '' }
  ];

  const videoTools = [
    { id: 'photo-to-video', name: 'Фото в Видео', iconKey: 'video', color: '#F59E0B', badge: '' },
    { id: 'video-merge', name: 'Видео монтаж', iconKey: 'cut', color: '#10B981', badge: 'Бесплатно' }
  ];

  // Скрытая админ-панель (рендерится только для владельца, см. isAdmin)
  const adminTools = [
    { id: 'admin', name: 'Аналитика', iconKey: 'chart', color: '#0EA5E9', adminOnly: true }
  ];

  // Метаданные инструмента для строки очереди (имя + глиф). Берём из тех же списков, что и меню.
  const findTool = (id) =>
    photoTools.find((t) => t.id === id) || videoTools.find((t) => t.id === id) || null;

  // TOOL_ACCENTS — источник правды по акцентам, но там нет vector-ai; падаем на цвет из меню.
  const toolAccent = (tool, id) => TOOL_ACCENTS[id] || (tool && tool.color) || COLORS.blue;

  const queueLabel = `${queue.length} ${pluralJobs(queue.length)} в работе`;

  const openJobThread = (job) => {
    setQueueAnchor(null);
    navigate(getToolPath(job.toolId));
  };

  // Функция рендеринга инструмента
  const renderTool = (tool) => {
    // Фильтруем auto-product-card для пользователей без доступа
    if (tool.id === 'auto-product-card' && !hasAccessToAutoProductCard()) {
      return null;
    }
    // Скрываем админ-панель от всех, кроме владельца
    if (tool.adminOnly && !isAdmin()) {
      return null;
    }

    const isActive = currentTool === tool.id;
    // Инструменты открыты всем — ведём на отдельную SEO-страницу /ai-photo/<slug>/
    // настоящей ссылкой (краулится). Скрытая админка остаётся на легаси-URL.
    const toolPath = tool.id === 'admin' ? '/ai-photo/?tool=admin' : getToolPath(tool.id);

    return (
      <ListItem key={tool.id} disablePadding sx={{ mb: '3px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        <Tooltip title={tool.name} placement="right" disableHoverListener={!isCollapsed}>
          <ListItemButton
            component={Link}
            to={toolPath}
            sx={{
              borderRadius: '12px',
              py: '9px',
              px: isCollapsed ? '9px' : '11px',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              bgcolor: isActive ? COLORS.blue : 'transparent',
              boxShadow: isActive ? SHADOWS.sidebarActive : 'none',
              '&:hover': { bgcolor: isActive ? COLORS.blue : COLORS.blueTile },
              transition: 'background 0.15s ease'
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 41, mr: 0, justifyContent: 'center' }}>
              <Box
                sx={{
                  bgcolor: isActive ? 'rgba(255,255,255,0.22)' : COLORS.blueTile,
                  width: 30,
                  height: 30,
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <ToolGlyph name={tool.iconKey} color={isActive ? '#fff' : COLORS.blue} />
              </Box>
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                className="sidebar-fade-in"
                sx={{ my: 0 }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#fff' : COLORS.name
                      }}
                    >
                      {tool.name}
                    </Typography>
                    {tool.badge && (
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: tool.badge === 'новое' || tool.badge === 'Бесплатно' ? COLORS.success : tool.badge === 'хит' ? COLORS.hit : tool.badge === 'old' ? '#9CA3AF' : '#6B7280',
                          color: 'white',
                          px: '6px',
                          py: '2px',
                          borderRadius: '6px',
                          fontSize: '10px',
                          fontWeight: 700,
                          lineHeight: 1.3
                        }}
                      >
                        {tool.badge}
                      </Typography>
                    )}
                  </Box>
                }
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_FULL_WIDTH,
        height: '100vh',
        bgcolor: '#fff',
        borderRight: `1px solid ${COLORS.cardBorder}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.divider}`, minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between' }}>
        {!isCollapsed && (
          <Box component={Link} to="/" className="sidebar-fade-in" sx={{ textDecoration: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="AIRun.Art Logo"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                flexShrink: 0
              }}
            />
            <Box sx={{ minWidth: 0, lineHeight: 1.15 }}>
              <Typography
                sx={{
                  fontSize: '19px',
                  fontWeight: 800,
                  lineHeight: 1.15,
                  background: GRADIENTS.wordmark,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                airun.art
              </Typography>
              <Typography sx={{ fontSize: '11px', color: COLORS.muted }}>
                Запусти искусство AI
              </Typography>
            </Box>
          </Box>
        )}
        <IconButton size="small" onClick={toggleCollapsed} sx={{ color: COLORS.textSecondary }}>
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        {/* Секция AI Фото */}
        {!isCollapsed && (
          <Typography
            className="sidebar-fade-in"
            sx={{
              display: 'block',
              px: '20px',
              pb: '8px',
              color: COLORS.sectionLabel,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em'
            }}
          >
            AI ФОТО
          </Typography>
        )}
        <List sx={{ py: 0, px: isCollapsed ? '6px' : '12px' }}>
          {photoTools.map(renderTool).filter(Boolean)}
        </List>

        {/* Разделитель между секциями */}
        {!isCollapsed && <Divider sx={{ my: 2, mx: 2 }} />}

        {/* Секция AI Видео */}
        {!isCollapsed && (
          <Typography
            className="sidebar-fade-in"
            sx={{
              display: 'block',
              px: '20px',
              pb: '8px',
              color: COLORS.sectionLabel,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em'
            }}
          >
            AI ВИДЕО
          </Typography>
        )}
        <List sx={{ py: 0, px: isCollapsed ? '6px' : '12px' }}>
          {videoTools.map(renderTool).filter(Boolean)}
        </List>

        {/* Разделитель перед скрытой админ-панелью (только для владельца) */}
        {isAdmin() && adminTools.length > 0 && (
          <Divider sx={{ my: 2, mx: 2 }} />
        )}

        {/* Скрытая админ-панель */}
        {isAdmin() && (
          <List sx={{ py: 0, px: isCollapsed ? '6px' : '12px' }}>
            {adminTools.map(renderTool).filter(Boolean)}
          </List>
        )}
      </Box>

      {/* 1o — индикатор очереди. Рендерится только когда есть активные задачи. */}
      {queue.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: isCollapsed ? '10px 0' : '10px 12px', display: 'flex', justifyContent: 'center' }}>
            {isCollapsed ? (
              /* Свёрнутый сайдбар (он же мобильный рельс): плитка со спиннером и бейджем. */
              <Tooltip title={queueLabel} placement="right">
                <ButtonBase
                  onClick={(e) => setQueueAnchor(e.currentTarget)}
                  aria-label={queueLabel}
                  sx={{ borderRadius: '9px' }}
                >
                  <Badge
                    badgeContent={queue.length}
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: COLORS.blue,
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 700,
                        minWidth: '16px',
                        height: '16px',
                        padding: '0 4px'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '9px',
                        bgcolor: COLORS.blueBg,
                        border: `1px solid ${COLORS.blueBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <QueueSpinner />
                    </Box>
                  </Badge>
                </ButtonBase>
              </Tooltip>
            ) : (
              /* Развёрнутый сайдбар: строка-плашка со спиннером, счётчиком и шевроном. */
              <ButtonBase
                onClick={(e) => setQueueAnchor(e.currentTarget)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  borderRadius: '12px',
                  bgcolor: COLORS.blueBg,
                  border: `1px solid ${COLORS.blueBorder}`,
                  textAlign: 'left',
                  '&:hover': { filter: 'brightness(0.98)' }
                }}
              >
                <QueueSpinner />
                <Typography
                  className="sidebar-fade-in"
                  sx={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: COLORS.blueText }}
                  noWrap
                >
                  {queueLabel}
                </Typography>
                <KeyboardArrowRight sx={{ fontSize: 16, color: COLORS.blueText, flexShrink: 0 }} />
              </ButtonBase>
            )}
          </Box>
        </>
      )}

      {/* 1p — поповер со списком активных задач. Пустого состояния нет: на нуле индикатора нет. */}
      <Popover
        open={isQueueOpen}
        anchorEl={queueAnchor}
        onClose={() => setQueueAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: '340px',
            ml: '10px',
            borderRadius: '16px',
            border: `1px solid ${COLORS.cardBorder}`,
            boxShadow: SHADOWS.cardHover,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: '6px' }}>
          {queue.map((job) => {
            const tool = findTool(job.toolId);
            const accent = toolAccent(tool, job.toolId);
            const toolName = (tool && tool.name) || job.toolId;
            return (
              <ButtonBase
                key={job.id}
                component="div"
                onClick={() => openJobThread(job)}
                sx={{
                  width: '100%',
                  display: 'block',
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: '12px',
                  '&:hover': { bgcolor: COLORS.blueTile }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  <Box
                    sx={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '9px',
                      bgcolor: COLORS.blueTile,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <ToolGlyph name={tool ? tool.iconKey : 'spark'} color={accent} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.name, lineHeight: 1.35 }}
                      noWrap
                    >
                      {job.prompt || toolName}
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: COLORS.textSecondary }} noWrap>
                      {`${toolName} · ${QUEUE_STATUS_TEXT[job.status] || job.status} · ${formatElapsed(Date.now() - (job.startedAt || 0))}`}
                    </Typography>
                  </Box>
                </Box>
                {/* Мини прогресс-бар. Неопределённый намеренно: реального процента у KIE нет,
                    опрос знает только «ещё не готово». */}
                <LinearProgress
                  sx={{
                    mt: '7px',
                    height: '3px',
                    borderRadius: '2px',
                    bgcolor: COLORS.segTrack,
                    '& .MuiLinearProgress-bar': { bgcolor: accent }
                  }}
                />
              </ButtonBase>
            );
          })}
        </Box>
        <Box sx={{ borderTop: `1px solid ${COLORS.divider}`, padding: '10px 14px' }}>
          <Typography sx={{ fontSize: '11.5px', lineHeight: 1.4, color: COLORS.muted }}>
            {QUEUE_FOOTER_TEXT}
          </Typography>
        </Box>
      </Popover>

      {/* Footer - User Menu */}
      <Divider />
      {/* Contact links: Telegram + Email icons before "Связаться с нами" */}
      {/* В свёрнутом (мобильном) сайдбаре иконки в столбик, иначе конвертик вылезает за край */}
      <Box sx={{ padding: isCollapsed ? '10px 0' : '10px 15px', display: 'flex', flexDirection: isCollapsed ? 'column' : 'row', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Telegram" placement="top">
          <IconButton
            component="a"
            href="https://telegram.me/airun_art"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ color: '#40B3E0', p: 0.5 }}
          >
            <svg
              width={isCollapsed ? 24 : 20}
              height={isCollapsed ? 24 : 20}
              viewBox="0 0 256 256"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g>
                <path d="M128,0 C57.307,0 0,57.307 0,128 L0,128 C0,198.693 57.307,256 128,256 L128,256 C198.693,256 256,198.693 256,128 L256,128 C256,57.307 198.693,0 128,0 L128,0 Z" fill="#40B3E0"></path>
                <path d="M190.2826,73.6308 L167.4206,188.8978 C167.4206,188.8978 164.2236,196.8918 155.4306,193.0548 L102.6726,152.6068 L83.4886,143.3348 L51.1946,132.4628 C51.1946,132.4628 46.2386,130.7048 45.7586,126.8678 C45.2796,123.0308 51.3546,120.9528 51.3546,120.9528 L179.7306,70.5928 C179.7306,70.5928 190.2826,65.9568 190.2826,73.6308" fill="#FFFFFF"></path>
                <path d="M98.6178,187.6035 C98.6178,187.6035 97.0778,187.4595 95.1588,181.3835 C93.2408,175.3085 83.4888,143.3345 83.4888,143.3345 L161.0258,94.0945 C161.0258,94.0945 165.5028,91.3765 165.3428,94.0945 C165.3428,94.0945 166.1418,94.5735 163.7438,96.8115 C161.3458,99.0505 102.8328,151.6475 102.8328,151.6475" fill="#D2E5F1"></path>
                <path d="M122.9015,168.1154 L102.0335,187.1414 C102.0335,187.1414 100.4025,188.3794 98.6175,187.6034 L102.6135,152.2624" fill="#B5CFE4"></path>
              </g>
            </svg>
          </IconButton>
        </Tooltip>
        <Tooltip title="info@airun.art" placement="top">
          <IconButton
            component="a"
            href="mailto:info@airun.art"
            size="small"
            sx={{ color: '#40B3E0', p: 0.5 }}
          >
            <EmailIcon fontSize={isCollapsed ? 'medium' : 'small'} />
          </IconButton>
        </Tooltip>
        {!isCollapsed && (
          <Typography
            variant="subtitle2"
            className="sidebar-fade-in"
            sx={{ fontWeight: 500, color: 'text.primary', ml: 0.5 }}
          >
            Связаться с нами
          </Typography>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: isCollapsed ? 1 : 3 }}>
        {!isLoggedIn ? (
          isCollapsed ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Вход" placement="right"><IconButton component={Link} to="/login/" size="small"><AccountCircle /></IconButton></Tooltip>
              <Tooltip title="Регистрация" placement="right"><IconButton component={Link} to="/register/" color="primary" size="small"><AccountCircle /></IconButton></Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button 
                component={Link} 
                to="/login/"
                variant="outlined"
                fullWidth
                sx={{ 
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)', borderColor: '#667eea' }
                }}
              >
                Вход
              </Button>
              <Button 
                component={Link} 
                to="/register/"
                variant="contained"
                fullWidth
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', '&:hover': { background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' } }}
              >
                Регистрация
              </Button>
            </Box>
          )
        ) : (
          <>
            {isCollapsed ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Tooltip 
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {userData?.credits || 0} <CreditsIcon size={16} />
                    </Box>
                  } 
                  placement="right"
                >
                  <IconButton component={Link} to="/billing/" size="small" color="primary"><Wallet /></IconButton>
                </Tooltip>
                <Tooltip title="Профиль" placement="right"><IconButton component={Link} to="/profile/" size="small"><AccountCircle /></IconButton></Tooltip>
                <Tooltip title="Выход" placement="right"><IconButton onClick={handleLogout} size="small" color="error"><Logout /></IconButton></Tooltip>
              </Box>
            ) : (
              <>
                {/* Пользователь: аватар + имя + баланс под именем + шестерёнка */}
                <Box className="sidebar-fade-in" sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: COLORS.indigo, color: 'white', fontWeight: 700, fontSize: 15 }}>
                    {getInitials(userData?.full_name)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.name }} noWrap>
                      {userData?.full_name || 'Пользователь'}
                    </Typography>
                    <Box
                      component={Link}
                      to="/billing/"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: COLORS.blue, fontSize: '12.5px', fontWeight: 700, '&:hover': { color: COLORS.blueText } }}
                    >
                      {userData?.credits || 0} <CreditsIcon size={14} />
                    </Box>
                  </Box>
                  <IconButton onClick={handleMenuOpen} size="small" sx={{ color: COLORS.muted, flexShrink: 0 }}>
                    <Settings sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                {/* Меню настроек */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <MenuItem component={Link} to="/profile/" onClick={handleMenuClose}>
                    <AccountCircle sx={{ mr: 2 }} />
                    Профиль
                  </MenuItem>
                  <MenuItem component={Link} to="/ai-photo/?tab=my" onClick={handleMenuClose}>
                    <PhotoLibrary sx={{ mr: 2 }} />
                    Мои фото
                  </MenuItem>
                  <MenuItem component={Link} to="/billing/" onClick={handleMenuClose}>
                    <CreditCard sx={{ mr: 2 }} />
                    Пополнение баланса
                  </MenuItem>
                  <Divider />
                  <MenuItem component={Link} to="/history/" onClick={handleMenuClose}>
                    <History sx={{ mr: 2 }} />
                    История операций
                  </MenuItem>
                  <MenuItem component={Link} to="/settings/" onClick={handleMenuClose}>
                    <Settings sx={{ mr: 2 }} />
                    Настройки
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <Logout sx={{ mr: 2 }} />
                    Выйти
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(SidebarMenu);
